import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblOcAssistanceGrants, getPblOcCitizen, startPblOcAssistanceSession } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCase, PblCitizenProfile, PblDocument, PblEvaluation, PblMeeting, PblOcAssistanceGrant } from '@/types/pbl';
import { translatePblCaseStatusLabel } from '@/utils/pblCaseStatus';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { formatPblDisplayValue, formatPblEnglishChineseName } from '@/utils/pblDisplay';
import { translatePblDisplayValue } from '@/utils/pblDisplayTranslation';
import { startPblAssistance } from '@/utils/pblAssistance';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { TitleBar } from '../OcManageCases/styles';
import { translateOcCitizenDocumentType, translateOcCitizenMeetingStatus } from './detailValueLabels';
import {
  CitizenPageShell,
  CitizenPanel,
  CitizenTable,
  CitizenTableScroll,
  DetailGrid,
  DetailLabel,
  DetailValue,
  EmptyText,
  SectionTitle,
} from './styles';
import { useTranslation } from 'react-i18next';

type PblTranslator = (key: string, options?: { defaultValue?: string }) => string;

const getCitizenName = (profile: PblCitizenProfile) =>
  formatPblEnglishChineseName(
    profile.citizen_full_name_en,
    profile.citizen_full_name_zh,
    profile.citizen_full_name || `Citizen #${profile.id}`
  );

const getCitizenEmail = (profile: PblCitizenProfile) => (profile.is_shadow_email ? '-' : profile.email || '-');

const getCaseDisplayId = (item: PblCase) => item.case_id || item.case_reference || String(item.id);

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const getAuthorizationRows = (profile: PblCitizenProfile, t: PblTranslator) => {
  const authorization = (profile.latest_authorization || profile.authorizations?.[0] || null) as any;

  if (!authorization) return [];

  return [
    [t('pbl.pagesHardcoded.ocCitizens.detail.office'), authorization.oc_office_name || profile.oc_office_name || '-'],
    [
      t('pbl.pagesHardcoded.ocCitizens.detail.assistingStaff'),
      authorization.assisting_staff_name || profile.assisting_staff_name || '-',
    ],
    [
      t('pbl.pagesHardcoded.ocCitizens.detail.officeContact'),
      authorization.office_contact || profile.office_contact || '-',
    ],
    [
      t('pbl.pagesHardcoded.ocCitizens.detail.informationTrueDeclared'),
      authorization.information_true_declared ? t('pbl.common.yes') : t('pbl.common.no'),
    ],
    [
      t('pbl.pagesHardcoded.ocCitizens.detail.enquirerSignedAt'),
      formatPblDisplayDate(authorization.enquirer_signed_at_display || authorization.enquirer_signed_at),
    ],
    [
      t('pbl.pagesHardcoded.ocCitizens.detail.witnessSignedAt'),
      formatPblDisplayDate(authorization.witness_staff_signed_at_display || authorization.witness_staff_signed_at),
    ],
  ];
};

const meetingTypeLabelKeys: Record<string, string> = {
  video: 'pbl.pagesHardcoded.ocCitizens.detail.meetingTypes.video',
  voice: 'pbl.pagesHardcoded.ocCitizens.detail.meetingTypes.voice',
};

const evaluationRoleLabelKeys: Record<string, string> = {
  citizen: 'pbl.pagesHardcoded.ocCitizens.detail.roles.citizen',
  pbl_lawyer: 'pbl.pagesHardcoded.ocCitizens.detail.roles.pblLawyer',
};

const translateMappedValue = (
  t: PblTranslator,
  value: string | null | undefined,
  labelKeys: Record<string, string>
) => {
  if (!value) return '-';

  const labelKey = labelKeys[value];
  return labelKey ? t(labelKey, { defaultValue: value }) : translatePblDisplayValue(t, value);
};

const getAllMeetings = (cases: PblCase[]) =>
  cases.flatMap((item) => (item.meetings || []).map((meeting) => ({ caseItem: item, meeting })));

const getAllDocuments = (cases: PblCase[]) =>
  cases.flatMap((item) => (item.documents || []).map((document) => ({ caseItem: item, document })));

const getAllEvaluations = (cases: PblCase[]) =>
  cases.flatMap((item) => (item.evaluations || []).map((evaluation) => ({ caseItem: item, evaluation })));

const PblOcCitizenDetail = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const { isMobile } = useResize();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<PblCitizenProfile | null>(null);
  const [assistanceGrants, setAssistanceGrants] = useState<PblOcAssistanceGrant[]>([]);
  const [isStartingAssistance, setIsStartingAssistance] = useState(false);

  useEffect(() => {
    const numericProfileId = Number(profileId);

    if (!Number.isFinite(numericProfileId)) {
      errorHandler(t('pbl.pagesHardcoded.ocCitizens.detail.invalidCitizenProfileId'));
      return;
    }

    let isMounted = true;

    setIsLoading(true);
    getPblOcCitizen(numericProfileId)
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.ocCitizens.detail.failedToLoadCitizenDetail'));
          return;
        }

        setProfile(response.data.data);
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocCitizens.detail.failedToLoadCitizenDetail'));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [profileId]);

  useEffect(() => {
    const numericProfileId = Number(profileId);
    if (!Number.isFinite(numericProfileId)) return;
    getPblOcAssistanceGrants(numericProfileId)
      .then((response) => setAssistanceGrants(response.data.data || []))
      .catch(() => setAssistanceGrants([]));
  }, [profileId]);

  const activeAssistanceGrant = assistanceGrants.find((grant) => grant.status === 'active' && !grant.revoked_at);
  const beginAssistance = async () => {
    if (!profile || !activeAssistanceGrant || isStartingAssistance) return;
    setIsStartingAssistance(true);
    try {
      const response = await startPblOcAssistanceSession(activeAssistanceGrant.id);
      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.backlogEnhancements.assistance.startFailed'));
        return;
      }
      const result = response.data.data;
      startPblAssistance(result.session.id, profile.id, result.session_token);
      successHandler(t('pbl.backlogEnhancements.assistance.started'));
      navigate(PATH.pbl.citizenCaseCreate);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.backlogEnhancements.assistance.startFailed'));
    } finally {
      setIsStartingAssistance(false);
    }
  };

  const cases = profile?.cases || [];
  const meetings = useMemo(() => getAllMeetings(cases), [cases]);
  const documents = useMemo(() => getAllDocuments(cases), [cases]);
  const evaluations = useMemo(() => getAllEvaluations(cases), [cases]);
  const authorizationRows = useMemo(() => (profile ? getAuthorizationRows(profile, t) : []), [profile, t]);

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.ocCitizens)}>
            {t('pbl.pagesHardcoded.ocCitizens.detail.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.ocCitizens.detail.homeCitizenList')}<strong>{profile ? getCitizenName(profile) : t('pbl.pagesHardcoded.ocCitizens.detail.citizenDetail')}</strong>
          </span>
        </TitleBar>
      }
    >
      <CitizenPageShell>
        {isLoading && !profile ? (
          <CitizenPanel>
            <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.loadingCitizenDetail')}</EmptyText>
          </CitizenPanel>
        ) : profile ? (
          <>
            <CitizenPanel>
              <SectionTitle>{t('pbl.pagesHardcoded.ocCitizens.detail.basicInformation')}</SectionTitle>
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="button"
                  size="md"
                  variant="primary"
                  rounded={false}
                  disabled={!activeAssistanceGrant || isStartingAssistance}
                  onClick={beginAssistance}
                >
                  {isStartingAssistance
                    ? t('pbl.backlogEnhancements.assistance.starting')
                    : t('pbl.backlogEnhancements.assistance.start')}
                </Button>
                {!activeAssistanceGrant ? (
                  <span style={{ marginLeft: 12 }}>{t('pbl.backlogEnhancements.assistance.authorizationRequired')}</span>
                ) : null}
              </div>
              <DetailGrid>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.name')}</DetailLabel>
                <DetailValue>{getCitizenName(profile)}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.englishName')}</DetailLabel>
                <DetailValue>{profile.citizen_full_name_en || '-'}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.chineseName')}</DetailLabel>
                <DetailValue>{formatPblDisplayValue(profile.citizen_full_name_zh)}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.hkid4Digits')}</DetailLabel>
                <DetailValue>{profile.identity_partial || '-'}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.phone')}</DetailLabel>
                <DetailValue>{profile.phone || '-'}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.email')}</DetailLabel>
                <DetailValue>{getCitizenEmail(profile)}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.residentialAddress')}</DetailLabel>
                <DetailValue>{profile.residential_address || '-'}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.office')}</DetailLabel>
                <DetailValue>{profile.oc_office_name || profile.oc_office?.name || profile.oc_office?.office_name || '-'}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.officeContact')}</DetailLabel>
                <DetailValue>{profile.office_contact || profile.oc_office?.contact || profile.oc_office?.office_contact || '-'}</DetailValue>
              </DetailGrid>
            </CitizenPanel>

            <CitizenPanel>
              <SectionTitle>
                {t('pbl.citizenAuthorizationCaseCreate.accountRegistration.authorizationModal.title', {
                  defaultValue: 'Authorization',
                })}
              </SectionTitle>
              {authorizationRows.length > 0 ? (
                <DetailGrid>
                  {authorizationRows.map(([label, value]) => (
                    <div key={label} style={{ display: 'contents' }}>
                      <DetailLabel>{label}</DetailLabel>
                      <DetailValue>{value || '-'}</DetailValue>
                    </div>
                  ))}
                </DetailGrid>
              ) : (
                <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noAuthorizationRecord')}</EmptyText>
              )}
            </CitizenPanel>

            <CitizenPanel>
              <SectionTitle>{t('pbl.pagesHardcoded.ocCitizens.detail.cases')}</SectionTitle>
              {cases.length > 0 ? (
                <CitizenTableScroll>
                  <CitizenTable>
                    <thead>
                      <tr>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.caseId')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.status')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.category')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.createdAt')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((item) => (
                        <tr key={item.id}>
                          <td>{getCaseDisplayId(item)}</td>
                          <td>{translatePblCaseStatusLabel(t, item.status, item.status)}</td>
                          <td>{translatePblDisplayValue(t, item.enquiry_category) || '-'}</td>
                          <td>{formatPblDisplayDate(item.created_at_display || item.created_at)}</td>
                          <td>
                            <Button
                              type="button"
                              size="md"
                              variant="primary"
                              rounded={false}
                              onClick={() => navigate(buildCaseDetailPath(item.id))}
                            >
                              {t('pbl.pagesHardcoded.ocCitizens.detail.detail')}</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CitizenTable>
                </CitizenTableScroll>
              ) : (
                <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noCases')}</EmptyText>
              )}
            </CitizenPanel>

            <CitizenPanel>
              <SectionTitle>{t('pbl.pagesHardcoded.ocCitizens.detail.meetings')}</SectionTitle>
              {meetings.length > 0 ? (
                <CitizenTableScroll>
                  <CitizenTable>
                    <thead>
                      <tr>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.caseId')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.type')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.status')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.scheduledAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meetings.map(({ caseItem, meeting }: { caseItem: PblCase; meeting: PblMeeting }) => (
                        <tr key={`${caseItem.id}-${meeting.id}`}>
                          <td>{getCaseDisplayId(caseItem)}</td>
                          <td>{translateMappedValue(t, meeting.meeting_type, meetingTypeLabelKeys)}</td>
                          <td>{translateOcCitizenMeetingStatus(t, meeting.status)}</td>
                          <td>{formatPblDisplayDate(meeting.scheduled_at_display || meeting.scheduled_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </CitizenTable>
                </CitizenTableScroll>
              ) : (
                <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noMeetings')}</EmptyText>
              )}
            </CitizenPanel>

            <CitizenPanel>
              <SectionTitle>{t('pbl.pagesHardcoded.ocCitizens.detail.documents')}</SectionTitle>
              {documents.length > 0 ? (
                <CitizenTableScroll>
                  <CitizenTable>
                    <thead>
                      <tr>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.caseId')}</th>
                        <th>{t('pbl.common.labels.title', { defaultValue: 'Title' })}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.type')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.createdAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(({ caseItem, document }: { caseItem: PblCase; document: PblDocument }) => (
                        <tr key={`${caseItem.id}-${document.id}`}>
                          <td>{getCaseDisplayId(caseItem)}</td>
                          <td>{document.title}</td>
                          <td>{translateOcCitizenDocumentType(t, document.document_type)}</td>
                          <td>{formatPblDisplayDate(document.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </CitizenTable>
                </CitizenTableScroll>
              ) : (
                <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noDocuments')}</EmptyText>
              )}
            </CitizenPanel>

            <CitizenPanel>
              <SectionTitle>{t('pbl.pagesHardcoded.ocCitizens.detail.ratingsFeedback')}</SectionTitle>
              <DetailGrid style={{ marginBottom: 18 }}>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.evaluationCount')}</DetailLabel>
                <DetailValue>{profile.rating_summary?.evaluation_count ?? evaluations.length}</DetailValue>
                <DetailLabel>{t('pbl.pagesHardcoded.ocCitizens.detail.averageRating')}</DetailLabel>
                <DetailValue>{profile.rating_summary?.average_rating ?? '-'}</DetailValue>
              </DetailGrid>
              {evaluations.length > 0 ? (
                <CitizenTableScroll>
                  <CitizenTable>
                    <thead>
                      <tr>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.caseId')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.role')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.rating')}</th>
                        <th>{t('pbl.pagesHardcoded.ocCitizens.detail.remarks')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluations.map(({ caseItem, evaluation }: { caseItem: PblCase; evaluation: PblEvaluation }) => (
                        <tr key={`${caseItem.id}-${evaluation.id}`}>
                          <td>{getCaseDisplayId(caseItem)}</td>
                          <td>{translateMappedValue(t, evaluation.evaluator_role, evaluationRoleLabelKeys)}</td>
                          <td>{evaluation.rating ?? '-'}</td>
                          <td>{evaluation.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </CitizenTable>
                </CitizenTableScroll>
              ) : (
                <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noRatingRecords')}</EmptyText>
              )}
            </CitizenPanel>
          </>
        ) : (
          <CitizenPanel>
            <EmptyText>{t('pbl.pagesHardcoded.ocCitizens.detail.noCitizenDetail')}</EmptyText>
          </CitizenPanel>
        )}
      </CitizenPageShell>
    </PageContainer>
  );
};

export default PblOcCitizenDetail;
